import React, { useState } from 'react';
import Modal from './Modal';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const errText = (err) =>
  err.response?.data?.error ||
  (err.response?.status === 429 ? 'Too many attempts. Please wait a minute and try again.'
                               : 'Something went wrong — please try again.');

export default function SecurityModal({ onClose }) {
  const { user, refreshUser, logout } = useAuth();
  // 'status' | 'setup' | 'recovery' | 'disable' | 'delete'
  const [step, setStep]               = useState('status');
  const [setupData, setSetupData]     = useState(null);
  const [recoveryCodes, setRecovery]  = useState([]);
  const [code, setCode]               = useState('');
  const [password, setPassword]       = useState('');
  const [error, setError]             = useState('');
  const [busy, setBusy]               = useState(false);

  const startSetup = async () => {
    setError(''); setBusy(true);
    try {
      const { data } = await authApi.mfaSetup();
      setSetupData(data);
      setCode('');
      setStep('setup');
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const { data } = await authApi.mfaEnable({ code: code.trim() });
      setRecovery(data.recoveryCodes);
      setStep('recovery');
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  const finishEnable = async () => { await refreshUser(); onClose(); };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await authApi.mfaDisable({ password });
      await refreshUser();
      onClose();
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await authApi.deleteAccount({ password });
      // Session is already dead server-side; clear the client, then hard-reload
      // to the landing page so no state from the deleted account survives (and
      // so they don't land on a sign-in form for an account that's now gone).
      logout();
      window.location.href = '/';
    } catch (err) { setError(errText(err)); setBusy(false); }
  };

  const errorBox = error && (
    <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)',
      borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 14 }}>{error}</div>
  );

  return (
    <Modal title="Security" onClose={onClose}>
      {step === 'status' && (
        <div>
          <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700 }}>Two-factor authentication</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.5 }}>
            Add a second step at sign-in using an authenticator app (Google Authenticator,
            Authy, 1Password, etc.). Status:{' '}
            <strong style={{ color: user?.mfaEnabled ? 'var(--green)' : 'var(--text3)' }}>
              {user?.mfaEnabled ? 'Enabled' : 'Disabled'}
            </strong>
          </div>
          {errorBox}
          {user?.mfaEnabled ? (
            <button className="btn" style={{ width: '100%' }} onClick={() => { setError(''); setPassword(''); setStep('disable'); }}>
              Disable two-factor
            </button>
          ) : (
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy} onClick={startSetup}>
              {busy ? 'Please wait…' : 'Enable two-factor'}
            </button>
          )}

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 18, paddingTop: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700, color: 'var(--red)' }}>Danger zone</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
              Deleting your account removes your household, budgets, and history.
            </div>
            <button className="btn" style={{ width: '100%', color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={() => { setError(''); setPassword(''); setStep('delete'); }}>
              Delete my account
            </button>
          </div>
        </div>
      )}

      {step === 'setup' && setupData && (
        <form onSubmit={confirmEnable}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
            1. Scan this QR code with your authenticator app.
          </div>
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <img src={setupData.qrDataUri} alt="Two-factor QR code" width={180} height={180}
              style={{ borderRadius: 8, background: '#fff', padding: 6 }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14, wordBreak: 'break-all' }}>
            Can't scan? Enter this key manually:<br />
            <code style={{ fontSize: 12, color: 'var(--text)' }}>{setupData.secret}</code>
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>2. Enter the 6-digit code to confirm</label>
            <input className="field-input" type="text" inputMode="numeric" autoFocus
              autoComplete="one-time-code" placeholder="123456"
              style={{ width: '100%', letterSpacing: 1 }} value={code}
              onChange={e => setCode(e.target.value)} />
          </div>
          {errorBox}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" style={{ flex: 1 }}
              onClick={() => { setStep('status'); setError(''); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={busy || !code.trim()}>
              {busy ? 'Verifying…' : 'Confirm'}
            </button>
          </div>
        </form>
      )}

      {step === 'recovery' && (
        <div>
          <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700, color: 'var(--green)' }}>
            Two-factor is now on.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
            Save these recovery codes somewhere safe. Each can be used once to sign in if you
            lose access to your authenticator. <strong>They won't be shown again.</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16,
            fontFamily: 'monospace', fontSize: 13, background: 'var(--bg2)', padding: 14, borderRadius: 8 }}>
            {recoveryCodes.map(c => <div key={c}>{c}</div>)}
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={finishEnable}>
            I've saved my codes
          </button>
        </div>
      )}

      {step === 'delete' && (
        <form onSubmit={confirmDelete}>
          <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700, color: 'var(--red)' }}>
            Delete your account?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
            You'll be signed out immediately and your account will be locked. After a{' '}
            <strong>30-day grace period</strong>, your household, budgets, transactions, and history
            are <strong>permanently deleted</strong>. Change your mind within those 30 days? Contact
            us and we can restore it.
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Enter your password to confirm</label>
            <input className="field-input" type="password" autoFocus autoComplete="current-password"
              style={{ width: '100%' }} value={password}
              onChange={e => setPassword(e.target.value)} />
          </div>
          {errorBox}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" style={{ flex: 1 }}
              onClick={() => { setStep('status'); setError(''); }}>Cancel</button>
            <button type="submit" className="btn btn-danger" style={{ flex: 1, borderColor: 'var(--red)' }}
              disabled={busy || !password}>
              {busy ? 'Deleting…' : 'Delete my account'}
            </button>
          </div>
        </form>
      )}

      {step === 'disable' && (
        <form onSubmit={confirmDisable}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
            Enter your password to turn off two-factor authentication.
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Password</label>
            <input className="field-input" type="password" autoFocus autoComplete="current-password"
              style={{ width: '100%' }} value={password}
              onChange={e => setPassword(e.target.value)} />
          </div>
          {errorBox}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" style={{ flex: 1 }}
              onClick={() => { setStep('status'); setError(''); }}>Cancel</button>
            <button type="submit" className="btn" style={{ flex: 1, color: 'var(--red)', borderColor: 'var(--red)' }}
              disabled={busy || !password}>
              {busy ? 'Please wait…' : 'Disable'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
